import type { ReactNode } from "react";
import agentNativeMark from "../assets/agent-native-mark-white.svg";
import agentNativeMarkSmall from "../assets/agent-native-mark-small-white.svg";

/**
 * Shared full-bleed shell for /login and /register -- split-screen,
 * matching the corporate brand identity system (docs/brand/agent-native
 * -brand-identity.html): the real Agent Native mark (the geometric
 * triangle/mountain logo, not a placeholder monogram -- see
 * src/assets/agent-native-mark-*.svg, from the official logo set),
 * achromatic platinum/slate palette, Playfair Display display type with
 * italic accents, Sora body copy, IBM Plex Mono overlines. Colors/fonts
 * come from the CSS variables ThemeProvider generates for the active
 * theme (see @agentnative/ui's theme/ThemeProvider.tsx and
 * theme/themes.ts's agentNativeTheme) rather than hardcoded hex, so this
 * stays correct if the theme is ever retuned. The mark itself is a
 * static asset (not theme-driven): it only ships white/black/teal cuts,
 * and white is correct against this layout's dark background
 * regardless of which UI theme is active.
 *
 * Deliberately NOT the gradient-glow "startup" look other apps in the
 * ecosystem (ATF, sparbz-cloud) use for their own login pages --
 * Agent Native's corporate identity is explicitly achromatic and
 * editorial, not colorful (brand spec: "Palette Rule: Agent Native
 * materials must never use [sub-brand] colors as accent colors").
 *
 * Rendered OUTSIDE AppLayout's nav chrome (see router.tsx) -- a
 * logged-out visitor on /login has no use for a nav bar linking to
 * pages they can't use yet, and showing one there was the actual
 * "unpolished default login" complaint this component fixes.
 */
/** Touch-friendly (>=44px) field height shared by the auth forms. */
export const authInputClass = "min-h-11";
/** Full-width, >=44px high submit button. */
export const authButtonClass = "min-h-11 w-full";
/** Secondary links under the form: 44px tap height and a visible keyboard focus ring. */
export const authLinkClass =
  "inline-flex min-h-11 items-center rounded px-1 hover:text-[var(--color-neutral-900)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-primary)]";
/** Wrapping row for those links so they never overflow a 320px screen. */
export const authLinkRowClass =
  "flex flex-wrap items-center justify-between gap-x-4 font-sans text-xs text-[var(--color-neutral-600)]";

export function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <div className="grid min-h-screen w-full max-w-full grid-cols-1 overflow-x-clip bg-[var(--color-neutral-100)] min-[900px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      {/* Left: brand panel -- hidden below 900px (the form stacks alone);
       * both grid columns are minmax(0,1fr) so wide content can never push the
       * form column past the viewport. */}
      <div className="relative hidden min-w-0 overflow-hidden min-[900px]:flex min-[900px]:flex-col min-[900px]:justify-between min-[900px]:p-10 xl:p-16">
        {/* Subtle diagonal crosshatch texture, taken directly from the
         * brand spec's body::before rule. */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(135deg, rgba(168,176,188,0.015) 1px, transparent 1px), linear-gradient(225deg, rgba(168,176,188,0.015) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <img src={agentNativeMark} alt="" className="h-8 w-auto" />
          <span className="font-sans text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-neutral-700)]">
            Agent Native
          </span>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-display mb-4 break-words text-4xl font-normal xl:text-5xl leading-[1.1] tracking-[-0.025em] text-[var(--color-neutral-900)]">
            Sign in with{" "}
            <em className="italic text-[var(--color-brand-primary)]">Agent Native</em>
          </h1>
          <p className="font-sans text-base font-light leading-[1.65] text-[var(--color-neutral-600)]">
            One identity across every app in the ecosystem -- accounting,
            project management, governance, and what comes next.
          </p>
        </div>
        <p className="font-mono relative z-10 text-[10px] font-medium uppercase tracking-[0.1em] text-[var(--color-neutral-600)]">
          Agent Native Console
        </p>
      </div>

      {/* Right: the actual form */}
      <div className="flex min-w-0 items-center justify-center px-4 py-8 sm:p-8">
        <div className="w-full min-w-0 max-w-sm">
          <div className="mb-8 flex flex-col items-center text-center min-[900px]:items-start min-[900px]:text-left">
            <div className="mb-6 flex items-center gap-3 min-[900px]:hidden">
              <img src={agentNativeMarkSmall} alt="" className="h-7 w-auto" />
            </div>
            <h2 className="font-display mb-1 text-2xl font-medium text-[var(--color-neutral-900)]">
              {title}
            </h2>
            <p className="font-sans text-sm text-[var(--color-neutral-600)]">{subtitle}</p>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
